// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import {teamsPageContexts, teamsThemes, teamsAppVersions, teamsAppIdsV2, teamsAppIdsV3} from '@/constants/teams.js'
import {openUrl} from '@/helpers/url.js'
import {useAppTheme} from '@/composables/use-app-theme.js'
import {useLocalStorage} from '@/composables/use-local-storage.js'
import {app, authentication, pages, dialog, appInstallDialog} from '@microsoft/teams-js'
import {ref, unref, computed, watchEffect} from 'vue'
import * as Sentry from '@sentry/vue'

//Global refs
const context = ref(null)
const theme = ref(teamsThemes.DEFAULT)
const hasInitializedTeams = ref(false)
const isInTeams = ref(false)

/**
 * Teams composable
 */
export function useTeams() {

  //Composables
  const {getFromLocalStorage, setInLocalStorage} = useLocalStorage()
  const {setLightTheme, setDarkTheme} = useAppTheme()

  //Get query params
  const queryParams = new URLSearchParams(window.location.search)

  //Computed refs
  const page = computed(() => context.value?.page)
  const teamsAppId = computed(() => context.value?.app?.appId?.idAsString)
  const isFullScreen = computed(() => page.value?.isFullScreen)
  const isTaskDialog = computed(() => page.value?.frameContext === teamsPageContexts.TASK)
  const userName = computed(() => context.value?.user?.displayName || context.value?.user?.userPrincipalName)
  const userEmail = computed(() => context.value?.user?.userPrincipalName)
  const userId = computed(() => context.value?.user?.id)
  const tenantId = computed(() => context.value?.user?.tenant?.id)

  //Determine versions
  const queryParamVersion = computed(() => Number(queryParams.get('teamsAppVersion') || queryParams.get('v') || 0))
  const localStorageVersion = computed(() => getFromLocalStorage('teamsAppVersion', null))
  const appIdBasedVersion = computed(() => {
    if (Object.values(teamsAppIdsV3).includes(teamsAppId.value)) {
      return teamsAppVersions.V3
    }
    else if (Object.values(teamsAppIdsV2).includes(teamsAppId.value)) {
      return teamsAppVersions.V2
    }
    return null
  })

  //Computed refs
  const teamsAppVersion = computed(() =>
    appIdBasedVersion.value ||
    queryParamVersion.value ||
    localStorageVersion.value
  )

  //Deep link context
  //SEE: https://learn.microsoft.com/en-us/microsoftteams/platform/concepts/build-and-test/deep-link-application?tabs=example1%2Cteamsjs-v2%2Cformat2%2Cspecific%2Cformat3%2Cformat4%2Cformat5#configure-deep-link-to-browse-within-your-app-manually
  //This comes from the subEntityId query parameter in the deeplink URL and can contain stringified JSON data
  const deepLink = computed(() => page.value?.subPageId)

  //Theme change handler
  const themeChangeHandler = value => {
    theme.value = value
    if (value === teamsThemes.DARK) {
      setDarkTheme()
    }
    else {
      setLightTheme()
    }
  }

  //Full screen change handler
  const fullScreenChangeHandler = value => {
    isFullScreen.value = value
  }

  //Close dialog handler
  const closeDialog = () => dialog.url.submit()

  //Initialize teams
  const initializeTeams = async () => {
    try {

      //Only needed once
      if (hasInitializedTeams.value) {
        return
      }

      //Initialize
      await app.initialize()

      //Get context
      const ctx = await app.getContext()

      //Set refs
      context.value = ctx
      isInTeams.value = true

      //Set theme
      themeChangeHandler(ctx?.app.theme)

      //Register handlers
      app.registerOnThemeChangeHandler(themeChangeHandler)
      pages.registerFullScreenHandler(fullScreenChangeHandler)

      //Notify
      app.notifyAppLoaded()
      app.notifySuccess()

      //Log
      setTimeout(() => {
        console.log(`App is running in Teams`)
        console.log(` – Teams app ID: ${teamsAppId.value}`)
        console.log(` – Teams app version: ${teamsAppVersion.value}`)
        console.log(` – Theme: ${theme.value}`)
        console.log(` – Deep link: ${deepLink.value || 'n/a'}`)
        console.log(` – Teams context:`, ctx)
      })

      //Update Sentry context
      Sentry.setContext('teams', {
        appId: teamsAppId.value,
        appVersion: teamsAppVersion.value,
        tenantId: tenantId.value,
        userId: userId.value,
        deepLink: deepLink.value || 'n/a',
        context: ctx,
      })
    }
    catch (error) {
      isInTeams.value = false
      if (error.message.match(/no parent window found/i)) {
        console.log(`App is not running in Teams`)
      }
      else {
        throw error
      }
    }
    finally {
      hasInitializedTeams.value = true
    }
  }

  //Get Teams SSO token
  const getTeamsAuthToken = () => authentication.getAuthToken()

  //Store version in local storage
  watchEffect(() => {
    if (queryParamVersion.value) {
      setInLocalStorage('teamsAppVersion', queryParamVersion.value)
    }
    else if (appIdBasedVersion.value) {
      setInLocalStorage('teamsAppVersion', appIdBasedVersion.value)
    }
  })

  //Prompt Teams app install
  const promptTeamsAppInstall = async appId => {
    appId = unref(appId)

    //Prompt via app install dialog
    if (appInstallDialog.isSupported()) {
      try {
        await appInstallDialog.openAppInstallDialog({appId})
        console.log('Teams app install dialog opened')
        return
      }
      catch (error) {
        console.error('Error prompting Teams app install:', error)
      }
    }

    //Fallback mechanism
    //SEE: https://learn.microsoft.com/en-us/microsoftteams/platform/concepts/build-and-test/deep-link-application#configure-deep-link-manually-using-the-app-id
    openUrl(`https://teams.microsoft.com/l/app/${appId}?tenantId=${tenantId.value}`)
  }

  //Expose interface
  return {
    initializeTeams,
    getTeamsAuthToken,
    promptTeamsAppInstall,
    context,
    deepLink,
    hasInitializedTeams,
    isInTeams,
    isFullScreen,
    isTaskDialog,
    closeDialog,
    userName,
    userEmail,
    userId,
    tenantId,
    teamsAppId,
    teamsAppVersion,
  }
}
